import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { MarkdownModule } from 'ngx-markdown';
import { MaterialModule } from 'src/app/material/material.module';
import { ShowDialogsComponent } from 'src/app/components/show-dialogs/show-dialogs.component';
import {
  DocumentationService,
  VersionDoc,
} from 'src/app/services/documentation.service';

export interface GuideEditorData {
  solutionId: string;
  versionId: string;
  solutionName?: string | null;
  versionName?: string | null;
  /** Tenant names already used on this version, to offer in "Visible to". */
  tenants?: string[];
  /** Present = edit mode. Content is fetched via view_url when missing. */
  doc?: VersionDoc;
}

/**
 * "Write guide" dialog (mockup D): markdown editor on the left, live
 * ngx-markdown preview on the right. Closes with the saved doc on success.
 */
@Component({
  selector: 'app-guide-editor-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, MaterialModule, MarkdownModule],
  template: `
    <div class="dlg">
      <div class="dlg-h">
        <div class="t">
          <span class="ic">
            <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor"
                 stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 20h9" />
              <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
            </svg>
          </span>
          <div>
            <h2>{{ isEdit ? 'Edit guide' : 'Write guide' }}</h2>
            <div class="sub">{{ contextLabel }}</div>
          </div>
        </div>
        <span class="dlg-x" matTooltip="Close" (click)="cancel()">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor"
               stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </span>
      </div>

      <div class="titlebar">
        <label>Title</label>
        <input
          type="text"
          [(ngModel)]="title"
          placeholder="e.g. How to use this solution"
          maxlength="255"
        />
        <label class="lbl-gap">Visible to</label>
        <mat-form-field class="audfield" appearance="outline" subscriptSizing="dynamic">
          <mat-select multiple [(ngModel)]="audience" placeholder="All subscribers">
            <mat-select-trigger>
              {{ audience.length ? audience.join(', ') + ' only' : 'All subscribers' }}
            </mat-select-trigger>
            <mat-option *ngFor="let tenant of tenants" [value]="tenant">
              {{ tenant }}
            </mat-option>
          </mat-select>
        </mat-form-field>
      </div>

      <div class="err" *ngIf="errorMsg">{{ errorMsg }}</div>

      <div class="split">
        <div class="edcol">
          <div class="colhead">
            Markdown
            <span class="mdhint">
              <code># heading</code><code>**bold**</code><code>- list</code><code>\`\`\`code\`\`\`</code>
            </span>
          </div>
          <textarea
            class="edit"
            spellcheck="false"
            [(ngModel)]="draft"
            [disabled]="loadingContent"
            [placeholder]="loadingContent ? 'Loading guide…' : 'Write your guide in markdown…'"
          ></textarea>
        </div>
        <div class="pvcol">
          <div class="colhead">Preview</div>
          <div class="doc-body">
            <markdown [data]="draft || ''"></markdown>
          </div>
        </div>
      </div>

      <div class="dlg-f">
        <span></span>
        <span class="btns">
          <button type="button" class="btn-ghost" (click)="cancel()">Cancel</button>
          <button
            type="button"
            class="btn-primary"
            [disabled]="saving || loadingContent"
            (click)="save()"
          >
            {{ saving ? 'Saving…' : 'Save guide' }}
          </button>
        </span>
      </div>
    </div>
  `,
  styleUrls: ['./guide-editor-dialog.component.scss'],
})
export class GuideEditorDialogComponent implements OnInit {
  title = '';
  draft = '';
  audience: string[] = [];
  tenants: string[] = [];

  saving = false;
  loadingContent = false;
  errorMsg = '';

  private initialSnapshot = '';

  constructor(
    public dialogRef: MatDialogRef<GuideEditorDialogComponent>,
    private docService: DocumentationService,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: GuideEditorData
  ) {}

  get isEdit(): boolean {
    return !!this.data?.doc;
  }

  get contextLabel(): string {
    const parts = [this.data?.solutionName, this.data?.versionName].filter(
      Boolean
    );
    const scope = parts.length ? parts.join(' · ') + ' — ' : '';
    return `${scope}shown to everyone who has this version`;
  }

  ngOnInit(): void {
    this.tenants = this.data?.tenants || [];

    const doc = this.data?.doc;
    if (doc) {
      this.title = doc.title || '';
      this.audience = doc.tenant_names || [];
      const missing = this.audience.filter((t) => !this.tenants.includes(t));
      if (missing.length) {
        this.tenants = [...this.tenants, ...missing].sort((a, b) => a.localeCompare(b));
      }
      if (doc.content !== undefined) {
        this.draft = doc.content || '';
        this.takeSnapshot();
      } else if (doc.view_url) {
        this.loadingContent = true;
        this.docService.getDoc(doc.view_url).subscribe({
          next: (response: any) => {
            const full = response?.msg ?? response;
            this.draft = full?.content || '';
            this.loadingContent = false;
            this.takeSnapshot();
          },
          error: async (error) => {
            this.loadingContent = false;
            this.errorMsg = await this.docService.parseError(
              error,
              'Unable to load the guide content'
            );
            this.takeSnapshot();
          },
        });
        this.takeSnapshot();
        return;
      }
    }
    this.takeSnapshot();
  }

  private takeSnapshot(): void {
    this.initialSnapshot = this.snapshot();
  }

  private snapshot(): string {
    return JSON.stringify([this.title, this.draft, this.tenantNames]);
  }

  private get tenantNames(): string[] | null {
    return this.audience.length ? this.audience : null;
  }

  get dirty(): boolean {
    return this.snapshot() !== this.initialSnapshot;
  }

  cancel(): void {
    if (!this.dirty) {
      this.dialogRef.close(false);
      return;
    }
    const confirmRef = this.dialog.open(ShowDialogsComponent, {
      data: {
        title: 'Discard changes?',
        message: 'Your unsaved edits to this guide will be lost.',
        confirmText: 'Discard',
        cancelText: 'Keep editing',
        danger: true,
      },
      disableClose: false,
    });
    confirmRef.afterClosed().subscribe((discard) => {
      if (discard) this.dialogRef.close(false);
    });
  }

  save(): void {
    this.errorMsg = '';
    if (!this.title.trim()) {
      this.errorMsg = 'Title is required.';
      return;
    }
    if (!this.draft.trim()) {
      this.errorMsg = 'The guide is empty — write some markdown first.';
      return;
    }

    const payload = {
      title: this.title.trim(),
      content: this.draft,
      tenant_names: this.tenantNames,
    };

    const request = this.isEdit
      ? this.docService.updateGuide(
          this.data.solutionId,
          this.data.versionId,
          this.data.doc!.id,
          payload
        )
      : this.docService.createGuide(
          this.data.solutionId,
          this.data.versionId,
          payload
        );

    this.saving = true;
    request.subscribe({
      next: (response: any) => {
        this.saving = false;
        this.dialogRef.close(response || true);
      },
      error: async (error) => {
        this.saving = false;
        this.errorMsg = await this.docService.parseError(
          error,
          'Unable to save the guide'
        );
      },
    });
  }
}
